import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import WarehouseOutlinedIcon from '@mui/icons-material/WarehouseOutlined';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Container,
    InputAdornment,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import logo from '../assets/logoWhite.svg';
import { RootState } from '../config/auth/store';
import { loginFailure, loginSuccess } from '../config/auth/userSlice';
import { URL_PREFIX } from '../constants/stores';
import { IUserSession } from '../interface/User';
import { getUserInfo } from '../Utils/getUserInfo';
import theme from '../theme/theme';
import useScreenMode from '../Hooks/useScreenMode';

const LoginPage: React.FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const screen = useScreenMode();

    const authenticatedUser = useSelector((state: RootState) => state.user.user);
    const isAuthenticated = !!authenticatedUser?.first_name;

    const [form, setForm] = useState({
        email: '',
        password: '',
    });

    const [errors, setErrors] = useState({
        email: '',
        password: '',
        submit: '',
    });

    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isAuthenticated) {
            navigate('/');
        }
    }, [isAuthenticated, navigate]);

    const validate = () => {
        const nextErrors = {
            email: '',
            password: '',
            submit: '',
        };

        if (!/^\S+@\S+\.\S+$/.test(form.email)) {
            nextErrors.email = 'Enter a valid email';
        }

        if (!form.password.trim()) {
            nextErrors.password = 'Password is required';
        }

        setErrors(nextErrors);
        return !nextErrors.email && !nextErrors.password;
    };

    const handleChange =
        (key: 'email' | 'password') =>
            (e: React.ChangeEvent<HTMLInputElement>) => {
                const value = e.target.value;
                setForm((prev) => ({ ...prev, [key]: value }));
                setErrors((prev) => ({ ...prev, [key]: '', submit: '' }));
            };

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validate()) return;

        setLoading(true);
        setErrors((prev) => ({ ...prev, submit: '' }));

        try {
            const response = await fetch(`${URL_PREFIX}/auth/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(form),
            });

            const data = await response.json();

            if (!response.ok) {
                const message = data?.message || 'Login failed. Please try again.';
                setErrors((prev) => ({ ...prev, submit: message }));
                dispatch(loginFailure(message));
                setLoading(false);
                return;
            }

            const sessionId = data.token;
            const userData = getUserInfo(sessionId) as IUserSession;

            if (!sessionId || !userData) {
                const message = 'Unable to start your session.';
                setErrors((prev) => ({ ...prev, submit: message }));
                dispatch(loginFailure(message));
                setLoading(false);
                return;
            }

            dispatch(loginSuccess({ sessionId, user: userData }));
            navigate('/');
        } catch (error: any) {
            const message = error?.message || 'Error logging in.';
            setErrors((prev) => ({ ...prev, submit: message }));
            dispatch(loginFailure(message));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100dvh',
                background:
                    'linear-gradient(135deg, #f4f7fb 0%, #e8eef6 45%, #dde7f1 100%)',
                display: 'flex',
                alignItems: 'center',
                py: { xs: 3, md: 4 },
            }}
        >
            <Container maxWidth="xl">
                <Paper
                    elevation={0}
                    sx={{
                        overflow: 'hidden',
                        borderRadius: 4,
                        border: '1px solid',
                        borderColor: 'divider',
                        backgroundColor: 'background.paper',
                    }}
                >
                    <Grid container>
                        <Grid
                            size={{ xs: 12, md: 6 }}
                            sx={{
                                px: { xs: 3, sm: 4, md: 6 },
                                py: { xs: 1.5, sm: 4, md: 6 },
                                background:
                                    'linear-gradient(160deg, #0f2d40 0%, #124866 55%, #1a6b8a 100%)',
                                color: '#fff',
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            <Stack spacing={{ xs: 2, md: 3 }} sx={{ width: '100%' }}>
                                <Box
                                    component="img"
                                    src={logo}
                                    alt="SVP Logistics"
                                    sx={{
                                        width: 172,
                                        height: 72,
                                        objectFit: 'contain',
                                        // bgcolor: '#fff',
                                        // borderRadius: 2,
                                        // ml: -1,
                                    }}
                                />

                                <Stack>
                                    <Typography
                                        variant="overline"
                                        sx={{ letterSpacing: 1.2, opacity: 0.85 }}
                                    >
                                        SVP LOGISTICS
                                    </Typography>

                                    {/* <Typography
                                        variant="h5"
                                        sx={{
                                            fontWeight: 800,
                                            lineHeight: 1.1,
                                            mt: 0.5,
                                            fontSize: { xs: '2rem', md: '2.6rem' },
                                        }}
                                    >
                                        SVP Logistics
                                    </Typography> */}

                                    <Typography
                                        variant="caption"
                                        sx={{ mt: 1.5, opacity: 0.9, maxWidth: 520 }}
                                    >
                                        Manage BOLs, trailers, billing workflows, carrier access, and
                                        delivery status updates in one operational workspace.
                                    </Typography>
                                </Stack>

                                <Stack spacing={1.5} direction={{ xs: 'row', md: 'column' }} justifyContent={'space-between'}>
                                    <FeatureRow
                                        icon={<ReceiptLongOutlinedIcon fontSize="small" />}
                                        text={screen.isXs ? "BOL management" : "Bill of Lading creation, updates, and approvals"}
                                    />
                                    <FeatureRow
                                        icon={<LocalShippingOutlinedIcon fontSize="small" />}
                                        text={screen.isXs ? "Trailer tracking" : "Trailer tracking, yard operations, and movement visibility"}
                                    />
                                    <FeatureRow
                                        icon={<WarehouseOutlinedIcon fontSize="small" />}
                                        text={screen.isXs ? "Carrier coordination" : "Carrier coordination, dispatch status, and logistics workflows"}
                                    />
                                </Stack>
                            </Stack>
                        </Grid>

                        <Grid
                            size={{ xs: 12, md: 6 }}
                            sx={{
                                p: { xs: 3, sm: 4, md: 6 },
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#fcfdff',
                            }}
                        >
                            <Card
                                elevation={0}
                                sx={{
                                    width: '100%',
                                    maxWidth: 460,
                                    borderRadius: 4,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    backgroundColor: 'background.paper',
                                }}
                            >
                                <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                                    <Stack spacing={3}>
                                        <Box>
                                            <Typography variant="h5" fontWeight={800}>
                                                Sign in
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                Access the SVP Logistics operations portal.
                                            </Typography>
                                        </Box>

                                        {errors.submit ? <Alert severity="error">{errors.submit}</Alert> : null}

                                        <Box component="form" onSubmit={handleLogin}>
                                            <Stack spacing={2}>
                                                <TextField
                                                    fullWidth
                                                    label="Email"
                                                    placeholder="Enter your email"
                                                    value={form.email}
                                                    onChange={handleChange('email')}
                                                    error={!!errors.email}
                                                    helperText={errors.email}
                                                    autoComplete="email"
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <EmailOutlinedIcon fontSize="small" />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />

                                                <TextField
                                                    fullWidth
                                                    label="Password"
                                                    placeholder="Enter your password"
                                                    type="password"
                                                    value={form.password}
                                                    onChange={handleChange('password')}
                                                    error={!!errors.password}
                                                    helperText={errors.password}
                                                    autoComplete="current-password"
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <LockOutlinedIcon fontSize="small" />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />

                                                <Button
                                                    type="submit"
                                                    variant="contained"
                                                    size="large"
                                                    disabled={loading}
                                                    sx={{
                                                        mt: 1,
                                                        py: 1.2,
                                                        borderRadius: 2,
                                                        textTransform: 'none',
                                                        fontWeight: 700,
                                                    }}
                                                >
                                                    {loading ? 'Signing in...' : 'Login'}
                                                </Button>
                                            </Stack>
                                        </Box>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Paper>
            </Container>
        </Box>
    );
};

function FeatureRow({
    icon,
    text,
}: {
    icon: React.ReactNode;
    text: string;
}) {

    return (
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems="center">
            <Box
                sx={{
                    width: 34,
                    height: 34,
                    borderRadius: '50%',
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: theme.palette.text.secondary,
                    border: '1px solid rgba(255,255,255,0.14)',
                    flexShrink: 0,
                }}
            >
                {icon}
            </Box>
            <Typography sx={{ opacity: 1, color: theme.palette.text.disabled }}>
                {text}
            </Typography>
        </Stack>
    );
}

export default LoginPage;
